'use client';

import type { Route } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  BarChart3,
  HelpCircleIcon,
  Settings,
  ChevronDown,
  Flame,
} from 'lucide-react';
import { usePathname } from 'next/navigation';
import { motion } from 'framer-motion';

interface WordleNavbarProps {
  date: Date;
  onPrevDay: () => void;
  onNextDay: () => void;

  autoRevealEnabled: boolean;
  onToggleAutoReveal: () => void;

  hardcoreEnabled: boolean;
  onToggleHardcore: () => void;
  hardcoreDisabled?: boolean;
  /** Le mot du jour a été terminé (gagné/perdu) en mode hardcore. */
  hardcoreCompleted?: boolean;
  /** Le mot à révéler quand `hardcoreCompleted`. */
  hardcoreWord?: string;
  /** Issue de la partie hardcore terminée : gagné (vert) vs perdu (rouge). */
  hardcoreWon?: boolean;

  onOpenStats: () => void;
  onOpenHowTo: () => void;
  onOpenSettings: () => void;
  onOpenDatePicker: () => void;
  locale?: string;
  strings: {
    prevDayLabel: string;
    nextDayLabel: string;
    howToPlayLabel: string;
    datePickerLabel: string;
    settingsLabel: string;
    statsLabel: string;
    autoLabel: string;
    hardcoreLabel: string;
    hardcoreDisabledHint: string;
    playedInHardcoreLabel: string;
  };
}

function formatDate(date: Date, locale?: string) {
  return date.toLocaleDateString(locale ?? 'fr-FR');
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function WordleNavbar({
  date,
  onPrevDay,
  onNextDay,
  autoRevealEnabled,
  onToggleAutoReveal,
  hardcoreEnabled,
  onToggleHardcore,
  hardcoreDisabled = false,
  hardcoreCompleted = false,
  hardcoreWord,
  hardcoreWon = false,
  onOpenStats,
  onOpenHowTo,
  onOpenSettings,
  onOpenDatePicker,
  locale,
  strings,
}: WordleNavbarProps) {
  const pathname = usePathname();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isToday = isSameDay(date, today);
  const currentLang = useMemo(() => {
    if (pathname?.startsWith('/en')) {
      return 'en';
    }
    if (locale?.toLowerCase().startsWith('en')) {
      return 'en';
    }
    return 'fr';
  }, [locale, pathname]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!langMenuRef.current?.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    }

    if (isLangOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLangOpen]);

  type AppRoute = '/' | '/en';

  const languages: Array<{
    code: 'fr' | 'en';
    label: string;
    href: AppRoute;
    flagSrc: string;
  }> = [
    {
      code: 'fr',
      label: 'Français',
      href: '/' as const,
      flagSrc: '/flags/fr.svg',
    },
    {
      code: 'en',
      label: 'English',
      href: '/en' as const,
      flagSrc: '/flags/gb.svg',
    },
  ];

  const defaultLanguage = languages[0]!;
  const currentLanguage =
    languages.find((lang) => lang.code === currentLang) ?? defaultLanguage;

  return (
    <nav className="w-full relative z-50 bg-neutral-900">
      {/* Rangée 1 — contrôles de jeu */}
      <div className="mx-auto flex h-14 max-w-[352px] items-center justify-between px-0 md:max-w-[470px] md:px-4">
        <button
          type="button"
          onClick={onPrevDay}
          aria-label={strings.prevDayLabel}
          className="
            h-9 w-9
            rounded-md
            bg-neutral-700
            flex items-center justify-center
            transition
            active:scale-95
            hover:bg-neutral-600
          "
        >
          <ChevronLeft className="h-6 w-6 text-white" />
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpenHowTo}
            aria-label={strings.howToPlayLabel}
            className="
              hidden md:flex
              h-7 w-8
              rounded-md
              bg-neutral-700
              text-neutral-300
              items-center justify-center
              transition
              hover:bg-neutral-600
              active:scale-95
            "
          >
            <HelpCircleIcon className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={onOpenStats}
            className="
              flex items-center gap-1.5
              rounded-md px-2.5 py-1
              text-xs font-medium
              transition
              active:scale-95
              bg-neutral-700 text-neutral-300
              hover:bg-neutral-600
            "
          >
            <BarChart3 className="h-4 w-4" />
            {strings.statsLabel}
          </button>

          <button
            type="button"
            onClick={onOpenDatePicker}
            className="
              text-base font-semibold text-white whitespace-nowrap
              px-2 py-1 rounded-md
              transition
              hover:bg-neutral-700
              active:scale-95
            "
            aria-label={strings.datePickerLabel}
          >
            {formatDate(date, locale)}
          </button>

          <button
            type="button"
            onClick={onToggleAutoReveal}
            className={`
              flex items-center gap-1.5
              rounded-md border border-dashed px-2.5 py-1
              text-xs font-medium
              transition
              active:scale-95
              ${autoRevealEnabled
                ? 'border-emerald-200/70 bg-emerald-600 text-white hover:bg-emerald-500'
                : 'border-neutral-400 bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
              }
            `}
          >
            <Sparkles className="h-4 w-4" />
            {strings.autoLabel}
          </button>

          <button
            type="button"
            onClick={onOpenSettings}
            aria-label={strings.settingsLabel}
            className="
              hidden md:flex
              h-7 w-8
              rounded-md
              bg-neutral-700
              text-neutral-300
              items-center justify-center
              transition
              hover:bg-neutral-600
              active:scale-95
            "
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>

        <button
          type="button"
          onClick={onNextDay}
          disabled={isToday}
          aria-label={strings.nextDayLabel}
          className={`
            hover:bg-neutral-600
            h-9 w-9
            rounded-md
            flex items-center justify-center
            transition
            ${isToday
              ? 'bg-neutral-700/40 text-neutral-400 cursor-not-allowed'
              : 'bg-neutral-700 active:scale-95'
            }
          `}
        >
          <ChevronRight className="h-6 w-6 text-white" />
        </button>
      </div>

      {/* Rangée 2 — mode : Hardcore (gauche) + langue (droite) */}
      <div className="relative mx-auto flex max-w-[352px] items-center justify-between border-t border-white/5 px-0 py-2 md:max-w-[470px] md:px-4">
        <button
          type="button"
          onClick={onToggleHardcore}
          disabled={hardcoreDisabled}
          aria-pressed={hardcoreEnabled}
          title={hardcoreDisabled ? strings.hardcoreDisabledHint : strings.hardcoreLabel}
          className={`
            flex items-center gap-1.5
            rounded-md px-2.5 py-1
            text-xs font-medium
            transition
            ${hardcoreDisabled
              ? 'cursor-not-allowed bg-neutral-800 text-neutral-500 opacity-60'
              : hardcoreEnabled
              ? 'bg-red-600 text-white hover:bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.7)] active:scale-95'
              : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600 active:scale-95'
            }
          `}
        >
          <motion.span
            aria-hidden="true"
            className="inline-flex"
            style={{ transformOrigin: 'bottom center' }}
            animate={
              hardcoreEnabled
                ? {
                    scale: [1, 1.18, 0.94, 1.12, 1],
                    rotate: [0, -7, 5, -3, 0],
                  }
                : { scale: 1, rotate: 0 }
            }
            transition={
              hardcoreEnabled
                ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.2 }
            }
          >
            <Flame className="h-4 w-4" />
          </motion.span>
          {strings.hardcoreLabel}
        </button>

        {hardcoreCompleted && (
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            title={`${strings.playedInHardcoreLabel} ${hardcoreWord ?? ''}`}
          >
            <span
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${
                hardcoreWon
                  ? 'border-emerald-500/40 bg-emerald-950/50 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.3)]'
                  : 'border-red-500/40 bg-red-950/50 text-red-300 shadow-[0_0_12px_rgba(239,68,68,0.3)]'
              }`}
              aria-label={`${strings.playedInHardcoreLabel} ${hardcoreWord ?? ''}`}
            >
              <Flame
                className={`h-3.5 w-3.5 shrink-0 ${
                  hardcoreWon ? 'fill-emerald-400/30' : 'fill-red-400/30'
                }`}
                aria-hidden="true"
              />
              <span className="truncate text-xs font-bold uppercase tracking-[0.2em]">
                {hardcoreWord}
              </span>
            </span>
          </div>
        )}

        <div ref={langMenuRef} className="relative">
          <button
            type="button"
            onClick={() => setIsLangOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={isLangOpen}
            className="
              flex items-center gap-2
              rounded-full border-2 border-neutral-800
              px-2.5 py-1.5
              text-sm text-white
              transition
              hover:border-neutral-600
              active:scale-[0.98]
            "
          >
            <Image
              src={currentLanguage.flagSrc}
              alt=""
              width={20}
              height={20}
              className="rounded-full"
            />
            <ChevronDownIcon isOpen={isLangOpen} />
          </button>

          {isLangOpen && (
            <div
              role="listbox"
              className="
                absolute right-0 top-11 z-50
                w-44
                rounded-xl
                bg-neutral-800
                border border-neutral-700
                shadow-lg
                p-2
              "
            >
              {languages.map((lang) => {
                const isActive = lang.code === currentLang;
                return (
                  <Link
                    key={lang.code}
                    href={lang.href as Route}
                    onClick={() => setIsLangOpen(false)}
                    className={`
                      flex items-center justify-between
                      rounded-lg px-2 py-2
                      text-sm
                      transition
                      ${isActive
                        ? 'bg-indigo-500/20 text-white'
                        : 'text-neutral-300 hover:bg-neutral-700'
                      }
                    `}
                  >
                    <div className="flex items-center gap-2">
                      <Image
                        src={lang.flagSrc}
                        alt=""
                        width={18}
                        height={18}
                        className="rounded-full"
                      />
                      {lang.label}
                    </div>
                    {isActive && (
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500 text-white text-xs">
                        ✓
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

function ChevronDownIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <span
      className={`
        inline-flex h-5 w-5 items-center justify-center
        text-neutral-300
        transition-transform
        ${isOpen ? 'rotate-180' : ''}
      `}
      aria-hidden="true"
    >
      <ChevronDown className="h-4 w-4" />
    </span>
  );
}
